import { Link } from '@tanstack/react-router';
import { ArrowRight, Download, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HeroBanner } from '@/features/home/components/hero-banner/hero-banner';
import { ImpactCard } from '@/features/home/components/impact-card/impact-card';
import { HeroSkeleton } from '@/features/home/components/hero/hero-skeleton';
import { useStatsOverviewQuery } from '@/features/home/queries/stats.queries';
import { formatNumber, formatPercent } from '@/lib/utils';
import type { BannerSlide } from '@/shared/types/api.types';

const BANNER_SLIDES: BannerSlide[] = [
  {
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1920&h=700&fit=crop',
    alt: 'Equipo médico profesional',
    title: 'Protegiendo tu salud',
    description: 'Comprometidos con el aseguramiento en salud',
  },
  {
    image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=1920&h=700&fit=crop',
    alt: 'Atención médica de calidad',
    title: 'Fortalecemos el sistema',
    description: 'En beneficio de los ciudadanos y del país',
  },
  {
    image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1920&h=700&fit=crop',
    alt: 'Profesionales de la salud',
    title: 'Miles confían en nosotros',
    description: 'Equipos médicos de todo el país',
  },
];

export function Hero(): React.ReactElement {
  const { data: overview, isLoading, refetch } = useStatsOverviewQuery();

  return (
    <>
      <div className="pt-14">
        <HeroBanner slides={BANNER_SLIDES} autoplayDelay={4000} />
      </div>

      <section
        id="inicio"
        className="from-primary/5 to-secondary/5 relative flex items-center bg-linear-to-br py-12"
      >
        <div className="relative z-10 container mx-auto px-6 lg:px-12 xl:px-16 2xl:px-24">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div className="space-y-6">
              <h1 className="text-foreground text-4xl leading-tight font-black lg:text-5xl">
                <span className="from-primary to-secondary bg-linear-to-r bg-clip-text text-transparent">
                  Protegiendo tu salud,
                </span>
                <br />
                <span className="from-primary to-secondary bg-linear-to-r bg-clip-text text-transparent">
                  fortalecemos el sistema
                </span>
              </h1>

              <p className="text-muted-foreground max-w-xl text-base leading-relaxed font-light lg:text-lg">
                Comprometidos con el aseguramiento en salud, en beneficio de los ciudadanos y del
                país.
              </p>

              <div className="flex flex-col gap-4 sm:flex-row">
                <Link to="/quienes-somos">
                  <Button className="shadow-strong transform transition-all duration-300 hover:-translate-y-1 hover:scale-105">
                    Descubre más
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/ensuass">
                  <Button
                    variant="outline"
                    className="border-secondary text-secondary shadow-medium hover:bg-secondary/10 hover:shadow-strong transform border-2 transition-all duration-300 hover:-translate-y-1 hover:scale-105"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Descargar ENSUASS
                  </Button>
                </Link>
              </div>

              <div>
                <p className="text-muted-foreground mb-4 text-xs font-medium">
                  MILES DE EQUIPOS CONFIAN EN NOSOTROS
                </p>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  {isLoading ? (
                    <HeroSkeleton />
                  ) : overview ? (
                    <>
                      <ImpactCard
                        value={formatNumber(overview.totalAfiliadosRegulares)}
                        label="Afiliados Regulares"
                        icon="users"
                      />
                      <ImpactCard
                        value={formatNumber(overview.totalAfiliadosPotestativos)}
                        label="Afiliados Potestativos"
                        icon="fileText"
                      />
                      <ImpactCard
                        value={formatNumber(overview.totalAfiliadosSCTR)}
                        label="Afiliados SCTR"
                        icon="shieldCheck"
                      />
                      <ImpactCard
                        value={formatPercent(overview.siniestralidadPromedio)}
                        label="Promedio Siniestralidad"
                        icon="activity"
                      />
                    </>
                  ) : (
                    <div className="col-span-full flex flex-col items-center gap-2 py-4">
                      <p className="text-muted-foreground text-sm">
                        No se pudieron cargar las estadísticas
                      </p>
                      <Button variant="outline-destructive" size="sm" onClick={() => refetch()}>
                        <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                        Reintentar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="relative flex justify-center lg:justify-end">
              <div className="relative w-full max-w-md">
                <div className="group relative transform transition-all duration-700 ease-out hover:scale-105">
                  <img
                    src="/hero.png"
                    alt="Equipo médico colaborando"
                    className="group-hover:shadow-3xl h-auto w-full rounded-3xl shadow-2xl transition-all duration-500"
                  />
                  <div className="from-foreground/30 via-foreground/40 to-foreground/30 absolute -bottom-8 left-1/2 h-8 w-80 -translate-x-1/2 rounded-full bg-linear-to-r opacity-30 blur-xl transition-opacity duration-500 group-hover:opacity-40" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
